import { Avatar } from '../avatar'
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../dropdown'
import { NavbarItem } from '../navbar'
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
  SidebarHeading,
} from '../sidebar'
import { SidebarLayout } from '../sidebar-layout'
import {
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/16/solid'
import {
  BuildingOffice2Icon,
  ClipboardDocumentCheckIcon,
  PresentationChartLineIcon,
  TableCellsIcon,
} from '@heroicons/react/24/solid';
import { Outlet, useLocation } from "react-router-dom";
import { useAuth0, LogoutOptions } from '@auth0/auth0-react';
import { useOne, BaseRecord } from '@refinedev/core';
// MODIFIED: Import createContext
import { useEffect, useState, createContext } from 'react';
import Loading from '../loading/loading';
import { Heading } from '../heading';
import { colors, semanticColors } from '../../theme';
import Footer from '../footer';

// --- INTERFACES (Expanded based on your sample code) ---
interface VhUserPeople {
  firstname: string;
  lastname:string;
  email?: string;
  phone?: string;
}

interface VhUser {
  id?: string | number;
  user_id?: string | number;
  people: VhUserPeople;
}

// Added for nested organization data
interface Organization {
  organization_id: string;
  organization_name: string;
  location_code: string;
  email: string | null;
  phone: string | null;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
}

// Expanded Facility interface
interface Facility {
  id: string | number; // Assuming `id` or `facility_id` exists
  facility_id: string;
  facility_name: string;
  phone: string | null;
  email: string | null;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
  status: string;
  created_at: string;
  logo: string | null;
  organizations: Organization; // Nested organization
  // Add any other fields you need from the API
  location_code: string;
  market_id: string;
  facility_type_id: string;
  lat: string | null;
  long: string | null;
}

interface MeDataWrapper extends BaseRecord {
  id: string | number;
  data: [Facility, VhUser];
}

// --- NEW: Create a Context to hold both facility and user data ---
interface FacilityContextType {
  facility: Facility | null;
  user: VhUser | null;
}

// Create context with a more stable reference for Vite Fast Refresh compatibility
const FacilityContextValue: FacilityContextType = { facility: null, user: null };
export const FacilityContext = createContext<FacilityContextType>(FacilityContextValue);

const Layout = () => {
  const { user: auth0User, logout, isLoading: auth0Loading } = useAuth0();
  const [user, setUser] = useState<VhUser | null>(null);
  const [facility, setFacility] = useState<Facility | null>(null);
  const location = useLocation();

  const { data: findMeResponse, isLoading: meLoading, error } = useOne<MeDataWrapper>({
    resource: 'people',
    id: 'me',
    queryOptions: {
      retry: 3,
      retryDelay: 1000,
    }
  });

  useEffect(() => {
    if (findMeResponse?.data && Array.isArray(findMeResponse.data)) {
      setFacility(findMeResponse.data[0]);
      setUser(findMeResponse.data[1]);
    }
  }, [findMeResponse]);

  const handleLogoutForButton = () => {
    const logoutOptions: LogoutOptions = {
      returnTo: window.location.origin
    };
    logout(logoutOptions);
  };

  // Generate user initials
  const getUserInitials = () => {
    if (user?.people?.firstname && user?.people?.lastname) {
      return `${user.people.firstname.charAt(0)}${user.people.lastname.charAt(0)}`.toUpperCase();
    } else if (auth0User?.name) {
      const nameParts = auth0User.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
      } else {
        return nameParts[0].charAt(0).toUpperCase();
      }
    }
    return 'U';
  };

  if (auth0Loading || meLoading) {
    return <Loading />;
  }

  if (error) {
    // Error loading user data
  }

  if (!auth0User) {
    return <Loading />;
  }

  return (
    // --- MODIFIED: Wrap the layout with the Context Provider ---
    <FacilityContext.Provider value={{ facility, user }}>
      <div className="min-h-screen flex flex-col">
        <SidebarLayout
        sidebar={
          <Sidebar>
            <SidebarHeader>
              <div className="flex flex-row items-center w-full">
                <Avatar 
                  className="size-12 p-1 flex-shrink-0" 
                  square={true} 
                  src="/image.png"
                  style={{ backgroundColor: 'transparent' }}
                />
                <div className="ml-2 flex flex-col min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <Heading 
                      level={4}
                      className="flex-shrink-0"
                      style={{ 
                        fontWeight: 'bold',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '1.5rem',
                        background: 'linear-gradient(to right, #F5B957, #ffedcd, #AB6D0A)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      VITAL HIVE
                    </Heading>
                    <span
                      className="whitespace-nowrap flex-shrink-0"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.7rem',
                        fontWeight: '300',
                        color: '#E5E7EB',
                        letterSpacing: '0.05em',
                        marginLeft: '13px',
                      }}
                    >
                        TECHNOLOGIES
                    </span>
                  </div>
                </div>
              </div>
            </SidebarHeader>
            
            <SidebarBody>
              <SidebarSection>
               
                <SidebarItem to="/dashboard" current={location.pathname === '/dashboard'}>
                  <PresentationChartLineIcon style={{ color: 'currentColor' }} />
                  <SidebarLabel>Dashboard</SidebarLabel>
                </SidebarItem>
                <SidebarItem to="/facility-shifts" current={location.pathname === '/facility-shifts'}>
                  <TableCellsIcon style={{ color: 'currentColor' }} />
                  <SidebarLabel>Facility Shifts</SidebarLabel>
                </SidebarItem>
                <SidebarItem to="/shift-calendar" current={location.pathname.startsWith('/shift-calendar')}>
                  <ClipboardDocumentCheckIcon style={{ color: 'currentColor' }} />
                  <SidebarLabel>Shift Calendar</SidebarLabel>
                </SidebarItem>
                <SidebarItem to="/facility-info" current={location.pathname === '/facility-info'}>
                  <BuildingOffice2Icon style={{ color: 'currentColor' }} />
                  <SidebarLabel>Facility Info</SidebarLabel>
                </SidebarItem>
              </SidebarSection>
              <SidebarSpacer />
            </SidebarBody>
            
            <SidebarFooter>
              <div 
                className="flex items-center space-x-3 rounded-xl p-3 transition-all hover:shadow-md"
                style={{ backgroundColor: semanticColors.background.tertiary }}
              >
                <div 
                  className="flex size-10 items-center justify-center rounded-full text-sm font-semibold text-white"
                  style={{ 
                    background: 'linear-gradient(135deg, #F5B957 0%, #AB6D0A 100%)',
                    boxShadow: '0 2px 8px rgba(245, 185, 87, 0.3)'
                  }}
                >
                  {getUserInitials()}
                </div>
                <div className="min-w-0 flex-1">
                  <div 
                    className="truncate text-sm font-medium"
                    style={{ color: semanticColors.text.primary }}
                  >
                    {user?.people ? `${user.people.firstname} ${user.people.lastname}` : auth0User?.name}
                  </div>
                  <div 
                    className="truncate text-xs"
                    style={{ color: semanticColors.text.secondary }}
                  >
                    {facility?.facility_name || 'Facility'}
                  </div>
                </div>
                <Dropdown>
                  <DropdownButton as={NavbarItem}>
                    <svg 
                      className="size-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      style={{ color: semanticColors.text.tertiary }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </DropdownButton>
                  <DropdownMenu className="min-w-48" anchor="bottom start">
                    <DropdownItem onClick={handleLogoutForButton}>
                      <ArrowRightStartOnRectangleIcon />
                      <DropdownLabel>Sign Out</DropdownLabel>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </SidebarFooter>
          </Sidebar>
        }
      >
        {/* The Outlet now has access to the facility context */}
        <div className="flex flex-col min-h-full">
          <div className="flex-1">
            <Outlet /> 
          </div>
          <Footer />
        </div>
      </SidebarLayout>
      </div>
    </FacilityContext.Provider>
  )
}

export default Layout;